import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency } from '@/utils/dataFormatters';
import { ENABLE_ASAAS } from '@/constants/asaas-pricing';
import { debugLogger } from '@/utils/debugLogger';

export interface AsaasPlanChangeHistory {
  id: string;
  old_plan: string | null;
  new_plan: string;
  billing_period: string | null;
  amount: number;
  effective_date: string;
  expires_at: string | null;
  status: string;
  created_at: string;
  type?: string;
  payment_method_type?: string | null;
  payment_status?: string | null;
}

export interface AsaasCurrentPlanInfo {
  plan_type: string;
  billing_period: string | null;
  current_plan_expires_at: string | null;
  scheduled_plan_change: string | null;
}

const formatPlanName = (planId: string): string => {
  const planNames: Record<string, string> = {
    'sponsor_free': 'Gratuito',
    'sponsor_standard': 'Standard',
    'sponsor_premium': 'Premium',
    'sponsored_free': 'Gratuito',
    'sponsored_premium': 'Premium',
    'free': 'Gratuito',
    'standard': 'Standard',
    'premium': 'Premium'
  };
  return planNames[planId] || planId;
};

const formatBillingPeriod = (period: string | null): string => {
  const periods: Record<string, string> = {
    'weekly': 'Semanal',
    'monthly': 'Mensal',
    'quarterly': 'Trimestral'
  };
  return periods[period || ''] || 'N/A';
};

const formatPaymentMethod = (method: string | null): string => {
  const methods: Record<string, string> = {
    'card': 'Cartão de Crédito',
    'pix': 'PIX',
    'boleto': 'Boleto',
    'bank_transfer': 'Transferência Bancária',
    'debit': 'Cartão de Débito'
  };
  return methods[method || ''] || method || 'Não informado';
};

const formatPaymentStatus = (status: string | null): string => {
  const statuses: Record<string, string> = {
    'succeeded': 'Pago',
    'pending': 'Pendente',
    'failed': 'Falhou',
    'canceled': 'Cancelado',
    'requires_action': 'Requer Ação'
  };
  return statuses[status || ''] || status || 'Não informado';
};

export const useAsaasPlanHistory = () => {
  const { user } = useAuth();

  // Only use this hook if Asaas is enabled
  const { data: asaasData, isLoading, refetch } = useQuery({
    queryKey: ['asaasHistory', user?.id],
    queryFn: async () => {
      if (!user?.id) return { planHistory: [], currentPlanInfo: null, scheduledChange: null };

      debugLogger.debug('asaas', 'Fetching Asaas history data...');
      const { data, error } = await supabase.functions.invoke('get-asaas-history');

      if (error) {
        debugLogger.error('asaas', 'Error fetching Asaas history:', error);
        throw error;
      }
      
      debugLogger.debug('asaas', 'Asaas history data received:', data);
      
      // Log Asaas environment info
      if (data?.asaasEnvironment) {
        debugLogger.debug('asaas', 'Environment info:', {
          environment: data.asaasEnvironment.environment,
          baseUrl: data.asaasEnvironment.baseUrl,
          function: 'get-asaas-history'
        });
      }
      
      return data;
    },
    enabled: !!user?.id && ENABLE_ASAAS,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0
  });

  const planHistory = asaasData?.planHistory || [];
  const currentPlanInfo = asaasData?.currentPlanInfo;
  const scheduledChange = asaasData?.scheduledChange;

  const formatHistoryData = (history: AsaasPlanChangeHistory[]) => {
    return history.map(change => ({
      ...change,
      old_plan_formatted: change.old_plan ? formatPlanName(change.old_plan) : 'Nenhum',
      new_plan_formatted: formatPlanName(change.new_plan),
      billing_period_formatted: formatBillingPeriod(change.billing_period),
      amount_formatted: formatCurrency(change.amount),
      effective_date_formatted: new Date(change.effective_date).toLocaleDateString('pt-BR'),
      expires_at_formatted: change.expires_at ? new Date(change.expires_at).toLocaleDateString('pt-BR') : null,
      created_at_formatted: new Date(change.created_at).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      payment_method_formatted: formatPaymentMethod(change.payment_method_type),
      payment_status_formatted: formatPaymentStatus(change.payment_status),
      asaas_payment_date: change.effective_date 
        ? new Date(change.effective_date).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : null
    }));
  };

  const formattedHistory = formatHistoryData(planHistory);

  const currentPlanFormatted = currentPlanInfo ? {
    ...currentPlanInfo,
    plan_type_formatted: formatPlanName(currentPlanInfo.plan_type),
    // Lógica defensiva: planos gratuitos não devem ter billing_period ou expires_at
    billing_period_formatted: currentPlanInfo.plan_type.includes('free') 
      ? 'Gratuito' 
      : formatBillingPeriod(currentPlanInfo.billing_period),
    expires_at_formatted: (currentPlanInfo.plan_type.includes('free') || !currentPlanInfo.current_plan_expires_at)
      ? null
      : new Date(currentPlanInfo.current_plan_expires_at).toLocaleDateString('pt-BR'),
    scheduled_plan_change_formatted: (currentPlanInfo.scheduled_plan_change && currentPlanInfo.scheduled_plan_change.trim() !== '') 
      ? formatPlanName(currentPlanInfo.scheduled_plan_change) 
      : null
  } : null;

  // Format scheduled change from plan_changes table
  const scheduledChangeFormatted = scheduledChange ? {
    ...scheduledChange,
    new_plan_formatted: formatPlanName(scheduledChange.new_plan),
    effective_date_formatted: new Date(scheduledChange.effective_date).toLocaleDateString('pt-BR'),
    expires_at_formatted: new Date(scheduledChange.expires_at).toLocaleDateString('pt-BR')
  } : null;

  return {
    planHistory: formattedHistory,
    currentPlanInfo: currentPlanFormatted,
    scheduledChange: scheduledChangeFormatted,
    hasHistory: planHistory.length > 0,
    isLoading,
    refetch
  };
};